/**
 * Lo de arriba de todo del home: el logo y el contacto.
 *
 * Hubo titulo y bajada debajo del logo (0093 los paso a la config). El local
 * los saco: el logo solo se lee mejor. Las columnas quedan en la base, pero ni
 * la tienda ni este dialogo las usan.
 *
 * WhatsApp e Instagram son los mismos de Configuracion, contra el mismo
 * `PATCH /config`. Estan duplicados a proposito: sin WhatsApp el home no tiene
 * boton de consulta, y mandar a la persona a otra pantalla en el medio de
 * armar el home es perder el hilo.
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { clavesTienda } from "@/components/tienda/consultas";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Cargando } from "@/components/ui/cargando";
import { Campo } from "@/components/ui/campo";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth-context";
import { mostrarError, mostrarExito } from "@/lib/errores";
import { srcImagen } from "@/lib/imagenes";
import { useActualizarConfig, useConfig } from "@/lib/queries";
import type { EmpresaConfig } from "@/lib/tipos";

import { BotonFoto } from "./BotonFoto";
import { clavesPublicacion, useQuitarLogo, useSubirLogo } from "./consultas";
import { LADO_LOGO } from "./logica";

const ID_WHATSAPP = "encabezado-whatsapp";

export interface DialogEncabezadoProps {
  abierto: boolean;
  onCerrar: () => void;
  /** Abre con el foco en el WhatsApp: el aviso de "falta el WhatsApp" lleva aca. */
  enfocarWhatsapp?: boolean;
}

/**
 * El dialogo. El formulario existe solo abierto y con la config cargada, asi
 * arranca siempre de lo guardado sin copiarlo en un efecto.
 */
export function DialogEncabezado({
  abierto,
  onCerrar,
  enfocarWhatsapp = false,
}: DialogEncabezadoProps): JSX.Element {
  const config = useConfig();
  const datos = config.data?.config;
  return (
    <Dialog open={abierto} onOpenChange={(v) => !v && onCerrar()}>
      <DialogContent
        mobile="hoja"
        onOpenAutoFocus={(e) => {
          if (!enfocarWhatsapp) return;
          e.preventDefault();
          document.getElementById(ID_WHATSAPP)?.focus();
        }}
      >
        {datos ? (
          <FormularioEncabezado datos={datos} onCerrar={onCerrar} />
        ) : (
          <Cargando texto="Cargando…" />
        )}
      </DialogContent>
    </Dialog>
  );
}

function FormularioEncabezado({
  datos,
  onCerrar,
}: {
  datos: EmpresaConfig;
  onCerrar: () => void;
}): JSX.Element {
  const { esAdmin } = useAuth();
  const qc = useQueryClient();
  const guardar = useActualizarConfig();
  const subirLogo = useSubirLogo();
  const quitarLogo = useQuitarLogo();

  const [whatsapp, setWhatsapp] = useState(datos.whatsapp ?? "");
  const [instagram, setInstagram] = useState(datos.instagram ?? "");
  async function alGuardar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    try {
      await guardar.mutateAsync({
        whatsapp,
        instagram,
      });
      // El home y los avisos de arriba ("falta el WhatsApp") leen de otro lado.
      void qc.invalidateQueries({ queryKey: clavesTienda.negocio });
      void qc.invalidateQueries({ queryKey: clavesPublicacion.resumen });
      mostrarExito("Encabezado guardado");
      onCerrar();
    } catch (error) {
      mostrarError(error);
    }
  }

  return (
    <form onSubmit={(e) => void alGuardar(e)} className="contents">
      <DialogHeader>
        <DialogTitle>El encabezado del home</DialogTitle>
        <DialogDescription>Es lo primero que lee el cliente al abrir el link.</DialogDescription>
      </DialogHeader>

      <DialogBody>
        {!esAdmin ? (
          <Aviso tono="info" compacto>
            Solo el administrador cambia el encabezado.
          </Aviso>
        ) : null}

        <Campo label="Logo" ayuda="Va arriba de todo, centrado.">
          <BotonFoto
            src={srcImagen(datos.logo_url)}
            nombre="el logo del local"
            lado={LADO_LOGO}
            tamano="grande"
            deshabilitado={!esAdmin}
            onSubir={(formulario) => subirLogo.mutateAsync(formulario)}
            onQuitar={() => quitarLogo.mutateAsync()}
          />
        </Campo>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo label="WhatsApp" ayuda="El botón «Escribinos» y cada consulta van a este número.">
            <Input
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              placeholder="3401413384"
              inputMode="tel"
              readOnly={!esAdmin}
              id={ID_WHATSAPP}
            />
          </Campo>
          <Campo label="Instagram">
            <Input
              value={instagram}
              onChange={(e) => setInstagram(e.target.value)}
              placeholder="@manzanamordida.arg"
              readOnly={!esAdmin}
            />
          </Campo>
        </div>
      </DialogBody>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCerrar}>
          {esAdmin ? "Cancelar" : "Cerrar"}
        </Button>
        {esAdmin ? (
          <Button type="submit" disabled={guardar.isPending}>
            {guardar.isPending ? "Guardando…" : "Guardar"}
          </Button>
        ) : null}
      </DialogFooter>
    </form>
  );
}
